import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import { SharedBaseMenuComponent } from '../shared-base-menu/shared-base-menu.component';
import { SharedMyButtonDirective } from '../../directives/shared-my-button.directive';
import { MyMenuItem } from '../../classes/MyMenuItem.interface';
import { Observable, Subject } from 'rxjs';
import { SharedMenuService } from '../../services/shared-menu.service';
import { takeUntil } from 'rxjs/operators';
import { MenuItemTypeEnum } from '../../classes/menu-item-type-enum';

@Component({
    selector: 'shared-sort-filter-menu',
    templateUrl: './shared-sort-filter-menu.component.html',
    styleUrls: ['./shared-sort-filter-menu.component.scss']
})
export class SharedSortFilterMenuComponent extends SharedBaseMenuComponent
    implements OnInit, OnDestroy {
    @Input() toggleMenuButton: HTMLButtonElement;
    private _destroySubject: Subject<void> = new Subject<void>();
    showMenu = false;
    // in order to use enum in html
    menuItemTypeEnum = MenuItemTypeEnum;
    destroy$: Observable<void> = this._destroySubject.asObservable();
    constructor(
        protected _renderer2: Renderer2,
        protected _changeDetectorRef: ChangeDetectorRef,
        protected _sharedMenuService: SharedMenuService
    ) {
        super(_renderer2, _changeDetectorRef);
    }

    ngOnInit() {
        this._sharedMenuService.closeAllMenus$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.showMenu) {
                    // We only call _closeMenu if the menu
                    // is open. Otherwise, there is a race condition between all
                    // Menus on the page where the last one's target button will
                    // be focused, rather than the target of the menu that was open.
                    this._closeMenu();
                }
            });
    }

    ngOnDestroy(): void {
        this._destroySubject.next();
        this._destroySubject.complete();
    }

    toggleShowMenu = (
        event: KeyboardEvent | MouseEvent,
        args: { command: any; commandArgs: any } = {
            command: null,
            commandArgs: null
        }
    ): void => {
        event.preventDefault();
        event.stopPropagation();
        // Toggles the View Resources menu
        this.showMenu = !this.showMenu;
        this._changeDetectorRef.detectChanges(); // Since we are accessing ViewChildren
        // which are contained inside of an ngIf, we must invoke changeDetection to
        // be able to detect the ViewChildren after the flag flips.
        if (!this.showMenu) {
            // re-focuses on the View Resources
            // button if the menu has just closed.
            const statusMenuButton = this.toggleMenuButton;
            this._renderer2.setAttribute(statusMenuButton, 'tabindex', '-1');
            if (statusMenuButton) {
                setTimeout(() => {
                    // This setTimeout has to be present for the view to re-render and the .focus call to work.
                    statusMenuButton.focus();
                    this._renderer2.removeAttribute(
                        statusMenuButton,
                        'tabindex'
                    );
                }, 0);
            }
        } else {
            const buttons: SharedMyButtonDirective[] = this.myButtons.toArray();
            const enabledIndexArray: number[] = this.menuOptions
                .map((option: MyMenuItem, index: number) => {
                    return !option.disabled ? index : null;
                })
                .filter((myIndex: number) =>
                    myIndex ? myIndex : myIndex === 0 ? true : false
                );
            if (buttons[enabledIndexArray[0]]) {
                buttons[enabledIndexArray[0]].elementRef.nativeElement.focus();
            }
        }
        if (args.command) {
            args.command(args.commandArgs);
        }
    }

    handleBlur = (event: FocusEvent) => {
        this.toggleShowMenu((event as unknown) as MouseEvent);
    }

    _closeMenu = () => {
        // Toggles the Grade dropdown menu
        this.showMenu = false;
        this._changeDetectorRef.detectChanges(); // Since we are accessing ViewChildren
        // which are contained inside of an ngIf, we must invoke changeDetection to
        // be able to detect the ViewChildren after the flag flips.

        // Re-focuses on the Grade button
        // button since the menu has just closed.
        const statusMenuButton = this.toggleMenuButton;
        this._renderer2.setAttribute(statusMenuButton, 'tabindex', '-1');
        if (statusMenuButton) {
            setTimeout(() => {
                // This setTimeout has to be present for the view to re-render and the .focus call to work.
                statusMenuButton.focus();
                this._renderer2.removeAttribute(statusMenuButton, 'tabindex');
            }, 0);
        }
    }

    onCbChange = (event: any, index: number, item: MyMenuItem) => {
        let shouldToggle = true;
        if (
            event.key === 'Enter' ||
            event.key === ' ' ||
            event.type === 'click'
        ) {
            // don't toggle the menu on enter/space/mouse click
            shouldToggle = false;
            item.checked = !!!item.checked;
            if (event.type === 'click') {
                // base component only handles keyboard events
                // so we have to handle it here
                item.command(item.checked);
            }
        }
        // forward to parent
        this.resetMenuFocus(
            event,
            index,
            shouldToggle,
            item.command,
            item.checked
        );
    }
}
